<!--
  @component ExportModal
  @description Modal for exporting items in various formats
-->
<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { t } from '@/i18n';
  import { zoteroService, ZoteroService } from '@services/zoteroService';
  import { notify } from '@services/notificationService';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import type { Item } from '@types';

  // Props
  export let visible = false;
  export let items: Item[] = [];

  const dispatch = createEventDispatcher<{
    close: void;
  }>();

  // Export format options
  const exportFormats = [
    { id: 'bibtex', name: 'BibTeX', ext: 'bib' },
    { id: 'biblatex', name: 'BibLaTeX', ext: 'bib' },
    { id: 'ris', name: 'RIS', ext: 'ris' },
    { id: 'csljson', name: 'CSL JSON', ext: 'json' },
    { id: 'mods', name: 'MODS', ext: 'xml' },
    { id: 'refer', name: 'Refer/BibIX', ext: 'txt' },
    { id: 'tei', name: 'TEI', ext: 'xml' },
    { id: 'wikipedia', name: 'Wikipedia', ext: 'txt' },
  ] as const;

  // Citation styles for bibliography
  const citationStyles = ZoteroService.CITATION_STYLES;

  // State
  type ExportMode = 'format' | 'bibliography';
  let exportMode: ExportMode = 'format';
  let selectedFormat: (typeof exportFormats)[number]['id'] = 'bibtex';
  let selectedStyle: string = 'apa';
  let isLoading = false;
  let previewContent = '';
  let showPreview = false;

  // Get selected format info
  $: currentFormat = exportFormats.find((f) => f.id === selectedFormat) || exportFormats[0];
  $: itemCount = items.length;

  // Handle export
  async function handleExport() {
    if (!$selectedLibraryId || !$selectedLibraryType || items.length === 0) {
      notify.warning($t('export.noItemsSelected'));
      return;
    }

    isLoading = true;
    let result;

    try {
      const itemKeys = items.map((i) => i.key);

      if (exportMode === 'bibliography') {
        result = await zoteroService.getBibliography(
          $selectedLibraryId,
          $selectedLibraryType,
          itemKeys,
          selectedStyle
        );
      } else {
        result = await zoteroService.exportItems(
          $selectedLibraryId,
          $selectedLibraryType,
          itemKeys,
          selectedFormat as (typeof ZoteroService.EXPORT_FORMATS)[number]
        );
      }

      if (result.success && result.data) {
        downloadContent(result.data);
        notify.success($t('export.exportSuccess'));
        handleClose();
      } else if (!result.success) {
        notify.error(result.error || $t('export.exportFailed'));
      }
    } catch {
      notify.error($t('export.exportFailed'));
    } finally {
      isLoading = false;
    }
  }

  // Handle copy to clipboard
  async function handleCopy() {
    if (!$selectedLibraryId || !$selectedLibraryType || items.length === 0) {
      notify.warning($t('export.noItemsSelected'));
      return;
    }

    isLoading = true;
    let result;

    try {
      const itemKeys = items.map((i) => i.key);

      if (exportMode === 'bibliography') {
        result = await zoteroService.getBibliography(
          $selectedLibraryId,
          $selectedLibraryType,
          itemKeys,
          selectedStyle
        );
      } else {
        result = await zoteroService.exportItems(
          $selectedLibraryId,
          $selectedLibraryType,
          itemKeys,
          selectedFormat as (typeof ZoteroService.EXPORT_FORMATS)[number]
        );
      }

      if (result.success && result.data) {
        await navigator.clipboard.writeText(result.data);
        notify.success($t('export.copiedToClipboard'));
      } else if (!result.success) {
        notify.error(result.error || $t('export.copyFailed'));
      }
    } catch {
      notify.error($t('export.copyFailed'));
    } finally {
      isLoading = false;
    }
  }

  // Handle preview
  async function handlePreview() {
    if (!$selectedLibraryId || !$selectedLibraryType || items.length === 0) {
      notify.warning($t('export.noItemsSelected'));
      return;
    }

    isLoading = true;
    let result;

    try {
      const itemKeys = items.map((i) => i.key);

      if (exportMode === 'bibliography') {
        result = await zoteroService.getBibliography(
          $selectedLibraryId,
          $selectedLibraryType,
          itemKeys,
          selectedStyle
        );
      } else {
        result = await zoteroService.exportItems(
          $selectedLibraryId,
          $selectedLibraryType,
          itemKeys,
          selectedFormat as (typeof ZoteroService.EXPORT_FORMATS)[number]
        );
      }

      if (result.success && result.data) {
        previewContent = result.data;
        showPreview = true;
      } else if (!result.success) {
        notify.error(result.error || $t('export.exportFailed'));
      }
    } catch {
      notify.error($t('export.exportFailed'));
    } finally {
      isLoading = false;
    }
  }

  // Download content as file
  function downloadContent(content: string) {
    const ext = exportMode === 'bibliography' ? 'html' : currentFormat.ext;
    const mimeType =
      exportMode === 'bibliography'
        ? 'text/html'
        : ext === 'json'
          ? 'application/json'
          : 'text/plain';

    const filename =
      items.length === 1
        ? `${items[0].key}.${ext}`
        : `export-${new Date().toISOString().slice(0, 10)}.${ext}`;

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Close modal
  function handleClose() {
    showPreview = false;
    previewContent = '';
    dispatch('close');
  }

  // Handle keyboard
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (showPreview) {
        showPreview = false;
      } else {
        handleClose();
      }
    }
  }

  // Handle backdrop click
  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  onMount(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

{#if visible}
  <div
    class="modal-backdrop"
    on:click={handleBackdropClick}
    on:keydown={handleKeydown}
    role="presentation"
  >
    <div class="modal">
      <div class="modal-header">
        <h2 id="export-modal-title">
          {itemCount === 1 ? $t('export.exportItem') : $t('export.exportItems')}
        </h2>
        <button class="close-button" on:click={handleClose} aria-label="Close"> &times; </button>
      </div>

      <div class="modal-body">
        {#if items.length === 0}
          <div class="empty-state">
            <p>{$t('export.noItemsSelected')}</p>
            <p class="hint">{$t('export.selectItemsFirst')}</p>
          </div>
        {:else}
          <!-- Item count -->
          <div class="info-bar">
            <span class="item-count">
              {$t('export.selectedItems', { count: itemCount })}
            </span>
          </div>

          <!-- Export mode tabs -->
          <div class="tabs">
            <button
              class="tab"
              class:active={exportMode === 'format'}
              on:click={() => {
                exportMode = 'format';
                showPreview = false;
              }}
            >
              {$t('export.format')}
            </button>
            <button
              class="tab"
              class:active={exportMode === 'bibliography'}
              on:click={() => {
                exportMode = 'bibliography';
                showPreview = false;
              }}
            >
              {$t('export.bibliography')}
            </button>
          </div>

          <!-- Format selection -->
          {#if exportMode === 'format'}
            <div class="form-group">
              <label for="export-format">{$t('export.formatLabel')}</label>
              <select id="export-format" bind:value={selectedFormat}>
                {#each exportFormats as format}
                  <option value={format.id}>{format.name}</option>
                {/each}
              </select>
            </div>
          {:else}
            <div class="form-group">
              <label for="citation-style">{$t('export.styleLabel')}</label>
              <select id="citation-style" bind:value={selectedStyle}>
                {#each citationStyles as style}
                  <option value={style.id}>{style.name}</option>
                {/each}
              </select>
            </div>
          {/if}

          <!-- Preview area -->
          {#if showPreview && previewContent}
            <div class="preview-container">
              <div class="preview-header">
                <span>{$t('export.preview')}</span>
                <button class="preview-close" on:click={() => (showPreview = false)}>
                  &times;
                </button>
              </div>
              <div class="preview-content">
                {#if exportMode === 'bibliography'}
                  {@html previewContent}
                {:else}
                  <pre>{previewContent}</pre>
                {/if}
              </div>
            </div>
          {/if}
        {/if}
      </div>

      {#if items.length > 0}
        <div class="modal-footer">
          <button class="btn btn-secondary" on:click={handlePreview} disabled={isLoading}>
            {isLoading ? $t('export.generating') : $t('export.preview')}
          </button>
          <button class="btn btn-secondary" on:click={handleCopy} disabled={isLoading}>
            {$t('export.copyToClipboard')}
          </button>
          <button class="btn btn-primary" on:click={handleExport} disabled={isLoading}>
            {isLoading ? $t('export.generating') : $t('export.download')}
          </button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    animation: fadeIn 0.15s ease-out;
  }

  .modal {
    background: var(--color-bg-secondary, #383838);
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    min-width: 500px;
    max-width: 700px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    animation: slideIn 0.2s ease-out;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border, #555);
  }

  .modal-header h2 {
    margin: 0;
    font-size: 18px;
    color: var(--color-text-primary, #e8e8e8);
  }

  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .close-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: var(--color-text-secondary, #aaa);
  }

  .empty-state p {
    margin: 0 0 8px;
  }

  .empty-state .hint {
    font-size: 13px;
    color: var(--color-text-muted, #888);
  }

  .info-bar {
    margin-bottom: 16px;
  }

  .item-count {
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  .tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 20px;
    border-bottom: 1px solid var(--color-border, #555);
  }

  .tab {
    background: none;
    border: none;
    padding: 10px 16px;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    font-size: 14px;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition:
      color 0.2s,
      border-color 0.2s;
  }

  .tab:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .tab.active {
    color: var(--color-accent, #5c7cfa);
    border-bottom-color: var(--color-accent, #5c7cfa);
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  .form-group select {
    width: 100%;
    padding: 10px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 14px;
  }

  .form-group select:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .preview-container {
    margin-top: 16px;
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    overflow: hidden;
  }

  .preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: var(--color-bg-tertiary, #4a4a4a);
    font-size: 12px;
    color: var(--color-text-secondary, #aaa);
  }

  .preview-close {
    background: none;
    border: none;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
  }

  .preview-content {
    max-height: 300px;
    overflow: auto;
    padding: 12px;
    background: var(--color-bg-primary, #2b2b2b);
    font-size: 12px;
  }

  .preview-content pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: monospace;
    color: var(--color-text-primary, #e8e8e8);
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid var(--color-border, #555);
  }

  .btn {
    padding: 10px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: var(--color-bg-tertiary, #4a4a4a);
    border: 1px solid var(--color-border, #555);
    color: var(--color-text-primary, #e8e8e8);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover, #555);
  }

  .btn-primary {
    background: var(--color-accent, #5c7cfa);
    border: 1px solid var(--color-accent, #5c7cfa);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-accent-hover, #4c6ef5);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
</style>
